<script>
import Layout from '@/router/layouts/main';
import Grid from './components/grid';
import Multiselect from 'vue-multiselect';
import statusMixin from '@/mixins/statusMixin';
import StatusMultiselect from '@/components/widgets/multiselects/status';
import FiltersCard from '@/components/widgets/cards/filters-search';
import FieldsMenu from '@/components/widgets/grids/grid-fields-menu.vue';

/**
 * Dashboard Component
 */
export default {
  page: {
    title: 'Inventory',
  },
  components: {
    Layout,
    Grid,
    Multiselect,
    StatusMultiselect,
    FiltersCard,
    FieldsMenu,
  },
  mixins: [statusMixin],
  data() {
    return {
      filters: null,
      searchText: '',
      selectedCategories: [],
      selectedSuppliers: [],
    };
  },
  created() {
    this.search();
  },
  watch: {
    $route() {
      this.search();
    },
  },
  methods: {
    search() {
      this.filters = {
        categoryTypeIdPublic:
          this.$route.name == 'inventoryPerCatTypeIdPublic'
            ? this.$route.params.catTypeIdPublic
            : null,
        statusCodes: this.selectedStatusIds,
        categoriesIdPublic: this.selectedCategoriesIds,
        suppliersIdPublic: this.selectedSuppliersIds,
        search: this.searchText,
      };
    },
    cancelSearch() {
      this.selectedCategories = [];
      this.selectedStatus = [];
      this.selectedSuppliers = [];
      this.searchText = '';

      this.search();
    },
  },
  computed: {
    fieldsComputed() {
      return [
        {
          key: 'code',
          label: this.$t('general.code'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('general.name'),
          sortable: true,
        },
        {
          key: 'statusOrder',
          label: this.$t('general.status'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'allocationName',
          label: this.$t('allocations.allocation'),
          class: ['centered-field', 'border-right'],
          sortable: true,
        },
        {
          key: 'orderRequestQuantity',
          label: this.$t('items.requestedNoEn'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'orderedQuantity',
          label: this.$t('items.orderedNoEn'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'reservedQuantity',
          label: this.$t('items.reserved'),
          class: ['centered-field', 'border-left'],
          sortable: true,
        },
        {
          key: 'totalQuantity',
          label: this.$t('items.total'),
          class: ['centered-field'],
          sortable: true,
        },
        {
          key: 'availableQuantity',
          label: this.$t('items.availables'),
          class: ['centered-field'],
          sortable: true,
        },
        {
          key: 'minQuantity',
          label: this.$t('items.min'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'categoryName',
          label: this.$t('categories.category'),
          class: ['centered-field', 'border-left'],
          sortable: true,
        },
        {
          key: 'buttons',
          label: this.$t(''),
          class: ['centered-field', 'border-left'],
        },
      ];
    },
    categories() {
      if (this.$route.name == 'inventoryPerCatTypeIdPublic') {
        return this.$store.getters['cache/getCategoriesByCategoryType'](
          this.$route.params.catTypeIdPublic
        );
      } else {
        return this.$store.state.cache.categories;
      }
    },
    suppliers() {
      return this.$store.state.cache.suppliers;
    },
    inventoryFiltersConfig: {
      get() {
        return this.$store.getters['localstorage/getByCode'](
          'inventoryFiltersConfig'
        );
      },
      set(value) {
        this.$store.dispatch('localstorage/setByCode', {
          code: 'inventoryFiltersConfig',
          config: value,
        });
      },
    },
    selectedStatus: {
      get() {
        return this.inventoryFiltersConfig.selectedStatus;
      },
      set(value) {
        this.inventoryFiltersConfig = {
          ...this.inventoryFiltersConfig,
          selectedStatus: value,
        };
      },
    },
    selectedCategoriesIds() {
      return this.selectedCategories != null
        ? this.selectedCategories.map((a) => a.idPublic)
        : [];
    },
    selectedSuppliersIds() {
      return this.selectedSuppliers != null
        ? this.selectedSuppliers.map((a) => a.idPublic)
        : [];
    },
    selectedStatusIds() {
      return this.selectedStatus != null
        ? this.selectedStatus.map((a) => a.statusCode)
        : [];
    },
  },
};
</script>

<template>
  <Layout
    :title="$t('inventory.inventory')"
    :no-card="true"
    @esc-double-pressed="cancelSearch"
  >
    <div class="flex-box">
      <b-input-group class="search-bar custom-inventory-shadow">
        <b-form-input
          id="searchInput"
          type="text"
          style="border: none"
          :placeholder="$t('general.search')"
          @keydown.escape.prevent="cancelSearch()"
          @keydown.enter.prevent="search()"
          v-model="searchText"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            style="border: none"
            variant="outline-light"
            @click="search()"
          >
            <i class="bx bx-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <FieldsMenu
        id="inventory-grid"
        class="flex-push"
        :dropup="true"
        :fields="fieldsComputed"
      />
    </div>

    <FiltersCard @search="search" @cancel="cancelSearch">
      <template #row>
        <b-row>
          <b-col sm="12" lg="4">
            <b-form-group
              id="group-suppliers"
              :label="$t('suppliers.suppliers')"
              label-class="no-bold"
              label-for="supplierSelect"
            >
              <multiselect
                id="supplierSelect"
                ref="multiselectSupplier"
                v-model="selectedSuppliers"
                :options="suppliers"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectSupplier)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>
          <b-col sm="12" lg="4">
            <b-form-group
              id="group-categories"
              :label="$t('categories.categories')"
              label-class="no-bold"
              label-for="categorySelect"
            >
              <multiselect
                id="categorySelect"
                ref="multiselectCategory"
                v-model="selectedCategories"
                :options="categories"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectCategory)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>
          <b-col sm="12" lg="4">
            <b-form-group
              id="group-status"
              :label="$t('general.status')"
              label-class="no-bold"
              label-for="statusSelect"
            >
              <StatusMultiselect
                :list="getInventoryStatus()"
                :defaultSelectedList="selectedStatus"
                @selection-change="(l) => (selectedStatus = l)"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </template>
    </FiltersCard>

    <Grid v-if="filters != null" :filters="filters" :fields="fieldsComputed" />
  </Layout>
</template>

<style scoped>
.search-bar {
  max-width: 500px;
  max-height: 40px;
  background-color: white;
  border-radius: 5px;
}
</style>